import { createFileRoute, redirect } from "@tanstack/react-router";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import { GoogleIcon } from "~/components/icons/google";
import { Button } from "~/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "~/components/ui/dialog";
import { Separator } from "~/components/ui/separator";
import { authClient } from "~/lib/auth-client";
import { getAuthSession } from "~/lib/server/functions/auth";
import { m } from "~/i18n/messages";

export const Route = createFileRoute("/")({
	beforeLoad: async () => {
		const { isAuthenticated } = await getAuthSession();
		if (isAuthenticated) throw redirect({ to: "/app" });
	},
	component: IndexPage,
});

function IndexPage() {
	const [isLoading, setIsLoading] = useState(false);
	async function handleSignInWithGoogle() {
		setIsLoading(true);
		await authClient.signIn.social({
			provider: "google",
			callbackURL: "/app",
		});
		setIsLoading(false);
	}

	return (
		<div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-t from-pink-50 to-white p-4">
			<h1 className="text-4xl md:text-6xl font-bold font-mochi mb-4 text-slate-800 relative">
				{m["app.name"]()}
				<div className="absolute py-1 px-3 bg-pink-100 rounded-sm text-pink-500 border border-pink-500 font-medium -right-4 top-2 text-xs rotate-6">
					{m["app.alpha_status"]()}
				</div>
			</h1>
			<p className="max-w-xl text-sm md:text-lg text-center text-slate-700 mb-4">
				{m["app.ai_tutor_description"]()}
			</p>
			<div className="flex gap-2 items-center">
				<Dialog>
					<DialogTrigger asChild>
						<Button disabled={isLoading}>{m["landing.try_it_button"]()}</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{m["landing.sign_in_title"]()}</DialogTitle>
							<DialogDescription>
								{m["landing.stay_logged_in_notice"]()}
							</DialogDescription>
						</DialogHeader>
						<Button
							variant="outline"
							className="flex items-center gap-2 cursor-pointer"
							onClick={handleSignInWithGoogle}
							disabled={isLoading}
						>
							{isLoading ? (
								<Loader2Icon className="size-4 animate-spin" />
							) : (
								<>
									<GoogleIcon />
									{m["landing.sign_in_with_google_button"]()}
								</>
							)}
						</Button>
					</DialogContent>
				</Dialog>
				<Dialog>
					<DialogTrigger asChild>
						<Button variant="secondary">{m["landing.what_is_title"]()}</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{m["landing.about_title"]()}</DialogTitle>
							<DialogDescription className="text-slate-700 text-base">
								<p className="mt-4">
									<strong className="text-slate-800 font-medium">
										MochiMemo
									</strong>{" "}
									{m["landing.app_description"]()}
								</p>
								<p className="mt-4">{m["landing.ai_learning_description"]()}</p>
								<Separator className="mt-4" />
								<p className="mt-4">
									<strong className="text-slate-800 font-medium">
										{m["landing.disclaimer.title"]()}
									</strong>{" "}
									{m["landing.disclaimer.full_text"]()}
								</p>
							</DialogDescription>
						</DialogHeader>
					</DialogContent>
				</Dialog>
			</div>
		</div>
	);
}
